import { Alert, Button, Loader, Modal, Stack, Text } from '@mantine/core';

export type InPersonRecordingModalPhase =
  | 'consent'
  | 'setting-up'
  | 'connecting-host'
  | 'joining'
  | 'failed';

export interface InPersonRecordingConsentModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  phase?: InPersonRecordingModalPhase;
  errorMessage?: string | null;
  hasPriorRecording?: boolean;
  /**
   * FHIR session-recording Consent gate (same as telehealth).
   * `null` while loading; `false` blocks Start; `true` allows verbal attestation + Start.
   */
  recordingConsentOnFile?: boolean | null;
}

const PROGRESS_STEPS: { phase: InPersonRecordingModalPhase; label: string }[] = [
  { phase: 'setting-up', label: 'Setting up recording…' },
  { phase: 'connecting-host', label: 'Opening secure recording connection…' },
  { phase: 'joining', label: 'Starting cloud recording…' },
];

function progressIndex(phase: InPersonRecordingModalPhase): number {
  const idx = PROGRESS_STEPS.findIndex((s) => s.phase === phase);
  return idx >= 0 ? idx : 0;
}

export function InPersonRecordingConsentModal({
  opened,
  onClose,
  onConfirm,
  phase = 'consent',
  errorMessage,
  hasPriorRecording = false,
  recordingConsentOnFile = null,
}: InPersonRecordingConsentModalProps): JSX.Element {
  const inProgress =
    phase === 'setting-up' || phase === 'connecting-host' || phase === 'joining';
  const failed = phase === 'failed';
  const consentLoading = recordingConsentOnFile == null;
  const consentMissing = recordingConsentOnFile === false;
  const canStart = recordingConsentOnFile === true;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Start in-person recording"
      centered
      closeOnClickOutside={!inProgress}
      closeOnEscape={!inProgress}
      withCloseButton={!inProgress}
    >
      <Stack gap="md">
        {phase === 'consent' ? (
          <>
            {consentLoading ? (
              <Stack gap="sm" align="center" py="sm">
                <Loader size="sm" />
                <Text size="sm" c="dimmed" ta="center">
                  Checking session recording consent…
                </Text>
              </Stack>
            ) : null}

            {consentMissing ? (
              <Alert color="red" title="Recording consent required">
                Session recording consent is not on file for one or more clients. Ask them to
                complete it in Settings or intake before recording.
              </Alert>
            ) : null}

            {canStart ? (
              <>
                <Text fw={600}>Have all parties present consented to a local audio recording?</Text>
                <Text size="sm" c="dimmed">
                  Recording consent is on file for every client in this session. Confirm that everyone
                  present today also agrees before you start.
                </Text>
                {hasPriorRecording ? (
                  <Text size="sm" c="orange.8">
                    This appointment already has a recording or transcript. Starting again will
                    overwrite the previous recording and transcript for this appointment.
                  </Text>
                ) : null}
              </>
            ) : null}

            <Stack gap="xs">
              <Button onClick={onConfirm} disabled={!canStart}>
                Start recording
              </Button>
              <Button variant="subtle" color="gray" onClick={onClose}>
                Cancel
              </Button>
            </Stack>
          </>
        ) : null}

        {inProgress ? (
          <Stack gap="sm" align="center" py="sm">
            <Loader size="md" />
            <Text fw={600} ta="center">
              {PROGRESS_STEPS[progressIndex(phase)]?.label}
            </Text>
            <Stack gap={4} w="100%">
              {PROGRESS_STEPS.map((step, index) => {
                const current = progressIndex(phase);
                const done = index < current;
                const active = index === current;
                return (
                  <Text
                    key={step.phase}
                    size="sm"
                    c={active ? 'gray.8' : done ? 'dimmed' : 'gray.5'}
                    fw={active ? 600 : 400}
                  >
                    {done ? '✓ ' : active ? '● ' : '○ '}
                    {step.label.replace(/…$/, '')}
                  </Text>
                );
              })}
            </Stack>
            <Text size="xs" c="dimmed" ta="center">
              This can take a little while. Keep this window open.
            </Text>
          </Stack>
        ) : null}

        {failed ? (
          <>
            <Text c="red" size="sm">
              {errorMessage?.trim() || 'Recording could not be started. You can try again.'}
            </Text>
            <Stack gap="xs">
              <Button onClick={onConfirm} disabled={recordingConsentOnFile === false}>
                Try again
              </Button>
              <Button variant="subtle" color="gray" onClick={onClose}>
                Close
              </Button>
            </Stack>
          </>
        ) : null}
      </Stack>
    </Modal>
  );
}
